import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Boxes, Library, Download, Lock, ExternalLink, ArrowLeft, Music, Video, Image as ImageIcon, FileText } from "lucide-react";
import AudioPlayer from "@/components/julie/AudioPlayer";
import VideoPlayer from "@/components/julie/VideoPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const TABS = [
  { id: "ebooks", label: "E-books", icon: BookOpen },
  { id: "programmes", label: "Programmes", icon: Boxes },
  { id: "livres", label: "Livres", icon: Library },
];

const priceLabel = (p) => `${Number(p || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const coverUrl = (path) => (path ? `${API}/media/${path}` : null);

// La boutique de Julie : e-books (PDF après achat), programmes complets débloqués
// à vie, livres physiques vers Amazon. Tout est vendu SÉPARÉMENT de l'abonnement.
export default function Boutique() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "ebooks";
  const [shop, setShop] = useState(null);
  const [busy, setBusy] = useState(null);
  const [program, setProgram] = useState(null); // programme ouvert (détail)
  const [programContent, setProgramContent] = useState(null); // contenu complet si acheté
  const [playingIdx, setPlayingIdx] = useState(null);

  const load = () => {
    const token = localStorage.getItem("rav-token");
    axios
      .get(`${API}/shop`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then(({ data }) => setShop(data))
      .catch(() => setShop({ enabled: false, ebooks: [], programs: [], books: [] }));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contenu complet d'un programme acheté (chemins des fichiers débloqués)
  useEffect(() => {
    setProgramContent(null);
    setPlayingIdx(null);
    if (!program?.owned) return;
    const token = localStorage.getItem("rav-token");
    axios
      .get(`${API}/programs/${program.id}/content`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setProgramContent(data.items || []))
      .catch(() => {});
  }, [program]);

  const buy = async (kind, item) => {
    const token = localStorage.getItem("rav-token");
    if (!token) {
      navigate("/connexion?mode=login");
      return;
    }
    if (busy) return;
    setBusy(item.id);
    try {
      const { data } = await axios.post(
        `${API}/payments/buy-item`,
        { kind, item_id: item.id, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      setBusy(null);
      toast(err.response?.data?.detail || "La page de paiement n'a pas pu s'ouvrir — réessayez.");
    }
  };

  // Le PDF n'est servi qu'après achat vérifié côté serveur (endpoint protégé)
  const downloadEbook = async (item) => {
    const token = localStorage.getItem("rav-token");
    try {
      const res = await axios.get(`${API}/ebooks/${item.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Téléchargement impossible pour le moment.");
    }
  };

  // Mini-guide PDF d'un programme acheté : servi par l'endpoint protégé, ouvert
  // dans un nouvel onglet (achat vérifié côté serveur)
  const openProgramPdf = async (prog, idx, title) => {
    const token = localStorage.getItem("rav-token");
    try {
      const res = await axios.get(`${API}/programs/${prog.id}/file/${idx}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast(`« ${title} » n'a pas pu s'ouvrir — réessayez.`);
    }
  };

  const kindIcon = (k) => (k === "video" ? <Video size={14} strokeWidth={1.6} /> : k === "image" ? <ImageIcon size={14} strokeWidth={1.6} /> : k === "pdf" ? <FileText size={14} strokeWidth={1.6} /> : <Music size={14} strokeWidth={1.6} />);

  if (!shop)
    return (
      <main className="rav-page" data-testid="boutique-page">
        <div className="rav-loading" data-testid="boutique-loading">
          <span className="rav-spinner" aria-hidden="true" />
          La Boutique s'habille de douceur…
        </div>
      </main>
    );

  return (
    <main className="rav-page rav-boutique" data-testid="boutique-page">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
        <h1 className="rav-page-title">La Boutique</h1>
        {/* Point d'accès clair à toutes les offres (demande Julie) */}
        <div className="rav-shop-links">
          <button type="button" className="rav-mini-card rav-shop-linkcard" data-testid="boutique-offres-link" onClick={() => navigate("/offres")}>
            <span className="rav-mini-card-title">Nos offres RAV</span>
            <span className="rav-mini-card-desc">Abonnements (7 jours offerts), forfaits et cartes cadeau.</span>
          </button>
          <button type="button" className="rav-mini-card rav-shop-linkcard" data-testid="boutique-rdv-link" onClick={() => navigate("/reservation")}>
            <span className="rav-mini-card-title">Prendre rendez-vous avec Julie</span>
            <span className="rav-mini-card-desc">En visio ou au cabinet, à votre rythme.</span>
          </button>
        </div>
        <p className="rav-page-sub">E-books, programmes et livres de Julie — en supplément de votre abonnement.</p>

        <div className="rav-chips rav-shop-tabs" data-testid="boutique-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`rav-chip${tab === t.id ? " is-selected" : ""}`}
              data-testid={`boutique-tab-${t.id}`}
              onClick={() => {
                setProgram(null);
                setSearchParams({ tab: t.id });
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ---------- E-BOOKS ---------- */}
        {tab === "ebooks" && (
          <div className="rav-shop-grid" data-testid="ebooks-list">
            {shop.ebooks.length === 0 && <p className="rav-page-sub">Les e-books arrivent bientôt.</p>}
            {shop.ebooks.map((e, i) => (
              <motion.div key={e.id} className="rav-shop-card" data-testid={`ebook-card-${i}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}>
                {coverUrl(e.cover_path) && <img className="rav-shop-cover" src={coverUrl(e.cover_path)} alt={e.title} />}
                <h3 className="rav-shop-title">{e.title}</h3>
                <p className="rav-shop-desc">{e.description}</p>
                <p className="rav-shop-price" data-testid={`ebook-price-${i}`}>{priceLabel(e.price)} <span className="rav-shop-ttc">TTC</span></p>
                {e.owned ? (
                  <button type="button" className="rav-primary-button" data-testid={`ebook-download-${i}`} onClick={() => downloadEbook(e)}>
                    <Download size={15} strokeWidth={1.7} /> Télécharger le PDF
                  </button>
                ) : (
                  <button type="button" className="rav-primary-button" data-testid={`ebook-buy-${i}`} disabled={busy === e.id} onClick={() => buy("ebook", e)}>
                    {busy === e.id ? "Ouverture sécurisée…" : `Acheter — ${priceLabel(e.price)}`}
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* ---------- PROGRAMMES ---------- */}
        {tab === "programmes" && !program && (
          <div className="rav-shop-grid" data-testid="programs-list">
            {shop.programs.length === 0 && <p className="rav-page-sub">Les programmes arrivent bientôt.</p>}
            {shop.programs.map((p, i) => (
              <motion.button key={p.id} type="button" className="rav-shop-card rav-shop-card-btn" data-testid={`program-card-${i}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }} onClick={() => setProgram(p)}>
                {coverUrl(p.cover_path) && <img className="rav-shop-cover" src={coverUrl(p.cover_path)} alt={p.title} />}
                <h3 className="rav-shop-title">{p.title}</h3>
                <p className="rav-shop-desc">{p.description}</p>
                <p className="rav-shop-price">{priceLabel(p.price)} <span className="rav-shop-ttc">TTC</span></p>
                <span className="rav-text-link">{p.owned ? "Ouvrir mon programme" : "Découvrir le programme"}</span>
              </motion.button>
            ))}
          </div>
        )}
        {tab === "programmes" && program && (
          <div className="rav-shop-detail" data-testid="program-detail">
            <button type="button" className="rav-text-link" data-testid="program-back" onClick={() => setProgram(null)}>
              <ArrowLeft size={14} strokeWidth={1.7} /> Tous les programmes
            </button>
            {coverUrl(program.cover_path) && <img className="rav-shop-cover rav-shop-cover-lg" src={coverUrl(program.cover_path)} alt={program.title} />}
            <h2 className="rav-shop-title" data-testid="program-title">{program.title}</h2>
            <p className="rav-shop-desc">{program.description}</p>
            <div className="rav-shop-items">
              {(program.owned ? programContent || [] : program.items || []).map((it, i) => (
                <div key={i} className="rav-shop-item" data-testid={`program-item-${i}`}>
                  <span className="rav-shop-item-label">
                    {program.owned ? kindIcon(it.kind) : <Lock size={13} strokeWidth={1.6} />}
                    {i + 1}. {it.title}
                  </span>
                  {program.owned && it.kind === "audio" && it.storage_path && (
                    <AudioPlayer
                      src={`${API}/programs/${program.id}/file/${i}`}
                      playing={playingIdx === i}
                      onToggle={() => setPlayingIdx(playingIdx === i ? null : i)}
                      testId={`program-audio-${i}`}
                    />
                  )}
                  {program.owned && it.kind === "video" && it.storage_path && (
                    <VideoPlayer
                      src={`${API}/programs/${program.id}/file/${i}`}
                      playing={playingIdx === i}
                      onToggle={() => setPlayingIdx(playingIdx === i ? null : i)}
                      onEnded={() => setPlayingIdx(null)}
                      testId={`program-video-${i}`}
                    />
                  )}
                  {program.owned && it.kind === "image" && it.storage_path && (
                    <img className="rav-session-image" data-testid={`program-image-${i}`} src={`${API}/programs/${program.id}/file/${i}`} alt={it.title} />
                  )}
                  {program.owned && it.kind === "pdf" && it.storage_path && (
                    <button type="button" className="rav-secondary-button rav-session-btn" data-testid={`program-pdf-${i}`} onClick={() => openProgramPdf(program, i, it.title)}>
                      <FileText size={14} strokeWidth={1.7} /> Ouvrir le guide (PDF)
                    </button>
                  )}
                </div>
              ))}
              {program.owned && programContent === null && <p className="rav-page-sub">Ouverture de votre programme…</p>}
            </div>
            {!program.owned && (
              <button type="button" className="rav-primary-button" data-testid="program-buy" disabled={busy === program.id} onClick={() => buy("program", program)}>
                {busy === program.id ? "Ouverture sécurisée…" : `Acheter le programme — ${priceLabel(program.price)}`}
              </button>
            )}
            {program.owned && <p className="rav-page-sub">Ce programme est à vous, pour toujours.</p>}
          </div>
        )}

        {/* ---------- LIVRES ---------- */}
        {tab === "livres" && (
          <div className="rav-shop-grid" data-testid="books-list">
            {shop.books.length === 0 && <p className="rav-page-sub">Les livres arrivent bientôt.</p>}
            {shop.books.map((b, i) => (
              <motion.div key={b.id} className="rav-shop-card" data-testid={`book-card-${i}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}>
                {coverUrl(b.cover_path) && <img className="rav-shop-cover" src={coverUrl(b.cover_path)} alt={b.title} />}
                <h3 className="rav-shop-title">{b.title}</h3>
                <p className="rav-shop-desc">{b.description}</p>
                <p className="rav-shop-price" data-testid={`book-price-${i}`}>{priceLabel(b.price)} <span className="rav-shop-ttc">TTC</span></p>
                <button
                  type="button"
                  className="rav-primary-button"
                  data-testid={`book-amazon-${i}`}
                  disabled={!b.amazon_url}
                  onClick={() => window.open(b.amazon_url, "_blank", "noopener,noreferrer")}
                >
                  <ExternalLink size={15} strokeWidth={1.7} /> Acheter sur Amazon
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </main>
  );
}
